import { Heading } from '@apollo/orbit';
import classnames from 'classnames';
import moment from 'moment';
import React, { useCallback } from 'react';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { BarChart } from 'src/components/charts/BarChart/BarChart';
import {
  MultiseriesData,
  getMultiSeriesValue,
} from 'src/components/charts/BarChart/mocks';
import { useTranslatedCursorPosition } from 'src/components/charts/helpers/CursorTranslator';
import { StackedTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { resolutionToSeconds } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { Resolution } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { possibleColors } from './errorGroupColors';
import { getRouteParamForGroupOption } from './getRouteParam';
import { GroupOption } from './GroupByButton';
import { useDisplayNameTranslator } from './useDisplayNameTranslator';

export function ErrorsChartTooltip({
  groupByDimension,
  resolution,
}: {
  groupByDimension: GroupOption;
  resolution: Resolution;
}) {
  const position = useTranslatedCursorPosition();
  const { translateToDisplayName } = useDisplayNameTranslator();
  const startTime = moment(position?.data.timestamp);
  const endTime = startTime
    .clone()
    .add(resolutionToSeconds(resolution), 'seconds');

  if (!position) {
    return null;
  } else if (position.stackIndex !== null) {
    const groupLabel = position?.data.stackCounts[position.stackIndex]?.label;
    const errorCount = position?.data.stackCounts[position.stackIndex]?.value;
    return (
      <div>
        <Heading className="text-white" size="lg">
          {translateToDisplayName(groupLabel, groupByDimension)}
        </Heading>
        <dl className="mt-2 grid grid-cols-1 gap-3 overflow-hidden">
          <div>
            <dt className="text-sm">Time</dt>
            <dd className="text-code">
              <p>
                <LocalTimestamp value={startTime} /> -{' '}
              </p>
              <p>
                <LocalTimestamp value={endTime} />
              </p>
            </dd>
          </div>
          <div>
            <dt className="text-sm">Errors</dt>
            <dd className="text-code">{format.number(errorCount, '0[.]0a')}</dd>
          </div>
        </dl>
      </div>
    );
  } else {
    return (
      <div>
        <dl className="mt-2 grid grid-cols-1 gap-3 overflow-hidden">
          <div>
            <dt className="text-sm">Time</dt>
            <dd className="text-code">
              <p>
                <LocalTimestamp value={startTime} /> -{' '}
              </p>
              <p>
                <LocalTimestamp value={endTime} />
              </p>
            </dd>
          </div>
          <div>
            <dt className="text-sm">Errors</dt>
            <dd className="text-code">
              {position.data.stackCounts.map((stack) => (
                <p key={stack.label}>
                  {translateToDisplayName(stack.label, groupByDimension)}:{' '}
                  {format.number(stack.value, '0[.]0a')}
                </p>
              ))}
            </dd>
          </div>
        </dl>
      </div>
    );
  }
}

interface Props<Data extends StackedTimeSeriesData[]> {
  className?: string;
  data: Data;
  loading?: boolean;
  tooltipContent?: React.ReactNode;
  groupByDimension: GroupOption;
}

export function ErrorsChart<Data extends StackedTimeSeriesData[]>({
  data,
  loading,
  tooltipContent,
  groupByDimension,
  className,
}: Props<Data>) {
  const trackCustomEvent = useTrackCustomEvent();
  const { setFilterParams } = useOperationInsightsFilter();

  const handleBarClick = useCallback(
    (barData: MultiseriesData, stackIndex: number | null) => {
      if (loading || stackIndex === null) {
        return;
      }
      trackCustomEvent({
        category: 'Operation Insights Errors',
        action: 'bar_chart_bar_click',
      });

      const errorsFilter = {
        [getRouteParamForGroupOption(groupByDimension)]:
          barData.stackCounts[stackIndex]?.label,
      };
      setFilterParams(insightsRouteConfig, errorsFilter);
    },
    [groupByDimension, loading, setFilterParams, trackCustomEvent],
  );

  return isMinLengthArray(1, data) ? (
    <BarChart
      className={classnames('h-80 py-6', { 'opacity-40': loading }, className)}
      data={data}
      getValue={getMultiSeriesValue}
      barColors={possibleColors.map((c) => ({
        fill: `rgb(${c} / 0.6)`,
        cursor: `rgb(${c} / 0.8)`,
        marker: `rgb(${c})`,
        muted: `rgb(${c} / 0.2)`,
      }))}
      tooltipContent={tooltipContent}
      onBarClick={handleBarClick}
    />
  ) : (
    <div className="h-80 rounded border border-primary py-6" />
  );
}
